import type { Dashboard, Debt, Transaction } from '@fin/shared';
import { useState, type ReactNode } from 'react';
import { del } from '../../lib/api.ts';
import { categoryLabel, useCategories, useWallets } from '../../lib/data.ts';
import { dateLong, kindLabels, money, weekday } from '../../lib/format.ts';
import { categoryColor, categoryIcon, kindTint, walletIcon } from '../../lib/icons.tsx';
import { invalidateMoney, useQuery } from '../../lib/query.ts';
import { Link } from '../../lib/router.tsx';
import { TransactionModal } from './screen.tsx';
import { TxRow } from './rows.tsx';
import { repeatDefaults } from './model.tsx';
import { Button, ConfirmButton, Icon, Modal, Tile, cx, toast } from '../../components/ui.tsx';

/**
 * An operation opened from a list: read first, change on purpose (Money Lover's transaction detail).
 * «Изменить» and «Повторить» replace the view with the operation sheet.
 */
export function TxDetail({ tx, onClose }: { tx: Transaction; onClose: () => void }) {
  const [mode, setMode] = useState<'view' | 'edit' | 'repeat'>('view');
  const wallets = useWallets();
  const counterpartCurrency = wallets.data?.find((w) => w.id === tx.counterpartWalletId)?.currency;
  if (mode === 'edit') return <TransactionModal tx={tx} onClose={onClose} />;
  if (mode === 'repeat') return <TransactionModal defaults={repeatDefaults(tx, counterpartCurrency)} onClose={onClose} />;
  return <TxView tx={tx} onClose={onClose} onEdit={() => setMode('edit')} onRepeat={() => setMode('repeat')} />;
}

function TxView({ tx, onClose, onEdit, onRepeat }: { tx: Transaction; onClose: () => void; onEdit: () => void; onRepeat: () => void }) {
  const cats = useCategories();
  const wallets = useWallets();
  const dash = useQuery<Dashboard>('/dashboard');
  const debt = useQuery<Debt & { transactions: Transaction[] }>(tx.debtId ? `/debts/${tx.debtId}` : null);
  const isTransfer = !!tx.transferId;
  const cat = cats.data?.find((c) => c.id === tx.categoryId);
  const wallet = wallets.data?.find((w) => w.id === tx.walletId);
  const other = wallets.data?.find((w) => w.id === tx.counterpartWalletId);
  const out = tx.amount < 0;
  const from = isTransfer ? (out ? wallet : other) : undefined;
  const to = isTransfer ? (out ? other : wallet) : undefined;
  const line = tx.periodItemId ? dash.data?.currentPeriod?.items.find((i) => i.id === tx.periodItemId) : undefined;
  const related = (debt.data?.transactions ?? []).filter((t) => t.id !== tx.id);
  // across currencies the rate is what was sent for what arrived
  const sentLeg = isTransfer ? (out ? { amount: -tx.amount, currency: tx.currency } : tx.counterpartAmount != null ? { amount: -tx.counterpartAmount, currency: tx.counterpartCurrency ?? '' } : null) : null;
  const gotLeg = isTransfer ? (out ? (tx.counterpartAmount != null ? { amount: tx.counterpartAmount, currency: tx.counterpartCurrency ?? '' } : null) : { amount: tx.amount, currency: tx.currency }) : null;
  const crossRate = sentLeg && gotLeg && sentLeg.currency !== gotLeg.currency && gotLeg.amount > 0 ? sentLeg.amount / gotLeg.amount : null;
  const tone = isTransfer ? '' : out ? 'neg' : 'pos';

  const remove = async () => {
    await del(`/transactions/${tx.id}`);
    invalidateMoney();
    onClose();
    toast(isTransfer ? 'Перевод удалён' : 'Операция удалена');
  };

  return (
    <Modal sheet title={isTransfer ? 'Перевод' : 'Операция'} onClose={onClose} className="modal-tx"
      footer={(
        <>
          <ConfirmButton size="md" onConfirm={remove} confirmText={isTransfer ? 'Удалить из обоих?' : 'Точно удалить?'}>Удалить</ConfirmButton>
          <div className="row-gap">
            <Button onClick={onRepeat}><Icon name="repeat" size={18} /> Повторить</Button>
            <Button variant="primary" onClick={onEdit}><Icon name="edit" size={18} /> Изменить</Button>
          </div>
        </>
      )}>
      <div className="txd-head">
        {isTransfer ? <Tile icon="transfer" color={kindTint.transfer} size="lg" />
          : <Tile icon={categoryIcon(cat ?? { name: tx.categoryName, kind: tx.categoryKind })} color={categoryColor(cat ?? { name: tx.categoryName, kind: tx.categoryKind })} size="lg" image={cat?.iconImage} />}
        <div className="txd-title">
          <span className="txd-name">{isTransfer ? 'Перевод между счетами' : cat ? categoryLabel(cat, cats.data ?? []) : tx.categoryName}</span>
          <span className={cx('txd-amount money', tone)}>{money(isTransfer ? Math.abs(tx.amount) : tx.amount, tx.currency, { sign: !isTransfer })}</span>
          {!isTransfer && tx.categoryKind !== 'expense' && tx.categoryKind !== 'income' ? <span className="muted small">{kindLabels[tx.categoryKind]}</span> : null}
        </div>
      </div>

      <ul className="txd-rows">
        {tx.note ? <Row icon="list">{tx.note}</Row> : null}
        <Row icon="calendar">{dateLong(tx.date, true)}<span className="muted">, {weekday(tx.date)}</span></Row>
        {isTransfer ? (
          <>
            <Row icon={from ? walletIcon(from) : 'wallet'} label="Откуда">{from?.name ?? '—'}</Row>
            <Row icon={to ? walletIcon(to) : 'wallet'} label="Куда">{to?.name ?? '—'}</Row>
            {crossRate !== null && sentLeg && gotLeg ? (
              <Row icon="transfer" label="Поступило и курс">
                <span className="money">{money(gotLeg.amount, gotLeg.currency)}</span>
                <span className="muted"> · 1 {gotLeg.currency} = <span className="money">{crossRate >= 1 ? crossRate.toFixed(2) : crossRate.toFixed(4)}</span> {sentLeg.currency}</span>
              </Row>
            ) : null}
          </>
        ) : <Row icon={wallet ? walletIcon(wallet) : 'wallet'} label="Кошелёк">{tx.walletName}</Row>}
        {tx.contactName ? <Row icon="users" label={tx.categoryKind === 'debt' || tx.categoryKind === 'debt_collection' ? 'У кого' : tx.categoryKind === 'loan' || tx.categoryKind === 'repayment' ? 'Кому' : 'С кем'}>{tx.contactName}</Row> : null}
        {debt.data ? (
          <Row icon="debt" label="Долг">
            <Link to={`/debts?id=${debt.data.id}`}>{debt.data.title}</Link>
            <span className="muted"> · осталось <span className="money">{money(debt.data.remaining, debt.data.currency)}</span></span>
          </Row>
        ) : null}
        {line ? <Row icon="budget" label="Строка плана">{line.name}</Row> : null}
        {tx.excludeFromReport && !isTransfer ? <Row icon="eye-off">Не учитывается в отчётах</Row> : null}
        {tx.sharedAmount > 0 ? <Row icon="users" label="Доли других"><span className="money">{money(tx.sharedAmount, isTransfer && tx.amount > 0 ? tx.counterpartCurrency ?? tx.currency : tx.currency)}</span></Row> : null}
        {isTransfer ? <Row icon="budget">{tx.periodIncome ? 'Добавлен к свободным деньгам периода' : tx.inPeriod ? 'Тратит деньги периода' : 'Не влияет на период'}</Row>
          : !tx.inPeriod && tx.amount < 0 ? <Row icon="budget">Не учитывается в лимите периода</Row>
          : tx.periodIncome && tx.amount > 0 ? <Row icon="budget">Добавлен к свободным деньгам периода</Row> : null}
      </ul>

      {related.length ? (
        <section className="txd-related" aria-labelledby="txd-related">
          <h3 id="txd-related">Связанные операции</h3>
          <ul className="tx-list">{related.map((t) => <TxRow key={t.id} t={t} showDate />)}</ul>
        </section>
      ) : null}
    </Modal>
  );
}

/** one fact of the operation: its icon on the icon line, the text on the 72px text line */
function Row({ icon, label, children }: { icon: string; label?: string; children: ReactNode }) {
  return (
    <li className="txd-row">
      <span className="txd-icon" aria-hidden><Icon name={icon} /></span>
      <span className="txd-text">
        {label ? <span className="txd-label">{label}</span> : null}
        <span>{children}</span>
      </span>
    </li>
  );
}
